<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col>
        <hm-spinner v-if="!ready"/>
        <b-form @submit.prevent="onSubmit">
          <b-card header="Training Event Details" v-show="ready">
            <Loading :active="busy" :is-full-page="false">
              <slot>
                <hm-spinner/>
              </slot>
            </Loading>
            <hm-alert/>

            <b-form-group label="When did the training occur?" label-class="font-weight-bold">
              <vue-datepicker
                name="date"
                placeholder="YYYY-MM-DD"
                format="yyyy-MM-dd"
                v-model="training.date"
                bootstrap-styling
                clear-button
                clear-button-icon="fa fa-times-circle"
                v-validate="'required'"
                :disabled="busy"
                :input-class="{ 'bg-white': true, 'is-invalid': errors.has('date') && submitted }"
              />
            </b-form-group>
            <b-form-group label="Who presented the training?" label-class="font-weight-bold">
              <b-form-input
                name="presenter"
                type="text"
                class="form-control"
                placeholder="Enter who presented, e.g., Dr. Jeff, Mary, Self (if read by oneself)"
                v-model="training.presenter"
                v-validate="'required'"
                :disabled="busy"
                :class="{ 'is-invalid': errors.has('presenter') && submitted }"
              />
            </b-form-group>
            <b-form-group label="Training description:" label-class="font-weight-bold">
              <b-form-textarea
                :rows="3"
                name="desc"
                class="form-control"
                placeholder="Optional training description, e.g., location, time, format (staff meeting, employee handout, newsletter, webinar, etc.)"
                v-model="training.desc"
              />
            </b-form-group>
            <b-form-group label="What topics were covered?" label-class="font-weight-bold">
              <div v-for="(value, key) in trainingMap.Training" :key="key">
                <hm-switch class="mx-1 p-1" size="sm" :value="key" :label="value.name" v-model="training.topics" data-type="check" :disabled="busy"/>
              </div>
            </b-form-group>
            <b-form-group label="Who was in attendance?" label-class="font-weight-bold">
              <div v-for="employee in activeWorkforce" :key="employee.id">
                <hm-switch class="mx-1 p-1" size="sm" :label="employee.name" :checked="isAttendee(employee)" @change="onToggleAttendee(employee)" data-type="check" :disabled="busy"/>
              </div>
              <div v-for="employee in extraWorkforce" :key="employee.id">
                <hm-switch class="mx-1 p-1" size="sm" :label="employee.name" :checked="isAttendee(employee)" @change="onToggleAttendee(employee)" data-type="check" :disabled="busy"/>
              </div>
            </b-form-group>

            <div slot="footer">
              <b-row>
                <b-col cols="6">
                  <b-button type="button" variant="secondary" class="px-4" :disabled="busy" :to="backLink">Back</b-button>
                  <b-button type="button" variant="danger" class="px-4 ml-2" :disabled="busy" v-show="id" @click="dlgDelete = true">Delete</b-button>
                </b-col>
                <b-col cols="6" class="text-right">
                  <b-button type="submit" variant="primary" class="px-4" :disabled="busy">Save</b-button>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-form>
      </b-col>
    </b-row>

    <!-- Delete Modal Dialog -->
    <b-modal id="dlgDelete" class="modal-primary" hide-header-close v-model="dlgDelete">
      <div class="font-weight-bold" slot="modal-title">
        <i class="fa fas fa-trash pr-2"></i>Delete Confirmation
      </div>
      <p>Are you sure you want to delete this training event?</p>
      <div slot="modal-footer">
        <b-button variant="default" @click="dlgDelete = false">No</b-button>
        <b-button variant="primary" class="ml-2" @click="dlgDelete = false; onDelete()">Yes</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { trainingService, workforceService } from '@/services'
import Enumerable from 'linq/linq.js'

export default {
  name: 'TrainingEdit',
  props: ['id'],
  data() {
    return {
      training: {
        id: '',
        date: new Date((new Date()).setHours(0, 0, 0, 0)), // today
        desc: '',
        topics: [],
        attendees: {}
      },
      workforce: [],
      extraWorkforce: [],
      dlgDelete: false,
      ready: false,
      busy: false,
      submitted: false
    }
  },
  computed: {
    ...mapState('maps', ['trainingMap', 'workforceMap']),
    activeWorkforce() {
      return Enumerable.from(this.workforce).where(x => { return x.active; }).toArray();
    },
    backLink() {
      return this.id ? '/training-list' : '/training-docs';
    }
  },
  methods: {
    ...mapActions({
      alertClear: 'alert/clear',
      alertError: 'alert/error',
      alertException: 'alert/exception'
    }),
    isAttendee(employee) {
      return this.training.attendees.hasOwnProperty(employee.id);
    },
    onToggleAttendee(employee) {
      if (this.training.attendees.hasOwnProperty(employee.id)) {
        this.$delete(this.training.attendees, employee.id);
      } else {
        this.$set(this.training.attendees, employee.id, employee.name);
      }
    },
    onSubmit() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      this.$validator.validate()
        .then(valid => {
          if (valid) {
            return trainingService.setTraining(this.training)
              .then(data => {
                this.$router.push(this.backLink);
              })
              .catch(err => {
                this.alertException({ err, message: 'Error saving training event.' });
              });
          } else {
            this.alertError('Please complete training information and submit again.');
          }
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onDelete() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      return trainingService.deleteTraining(this.training)
        .then(data => {
          this.$router.push('/training-list');
        })
        .catch(err => {
          this.alertException({ err, message: 'Error deleting training event.' });
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onLoad() {
      this.ready = false;

      Promise.all([
        this.$store.dispatch('maps/fetchTrainingMap'),
        workforceService.getWorkforce()
      ]).then((values) => {
        if (this.id) {
          return trainingService.getTraining(this.id)
            .then(data => {
              data.date = new Date(data.date);
              if (!data.topics) {
                data.topics = [];
              }

              // training must be set before workforce and extra workforce so reactivity and checkboxes work properly
              this.training = data;

              this.workforce = (values[1] && values[1].length > 0) ? values[1] : [];

              // build list of attendees that are no longer active workforce members
              this.extraWorkforce = Enumerable.from(data.attendees)
                .groupJoin(this.workforce,
                  function (o) { return o.key; },
                  function (i) { return i.id; },
                  function (o, i) {
                    let x = i.toArray();
                    if (x.length > 0) {
                      return { id: o.key, name: o.value, active: x[0].active };
                    }
                    return { id: o.key, name: o.value, active: false };
                  })
                .where(function (x) { return !x.active; })
                .toArray();

            })
            .catch(err => {
              this.alertException({ err, message: 'Error loading training item.' });
            });
        } else {
          this.workforce = (values[1] && values[1].length > 0) ? values[1] : [];
          return this.item;
        }
      }).catch((err) => {
        this.alertException({ err, message: 'Error loading data.' });
      }).finally(() => {
        this.ready = true;
      })
    }
  },
  created() {
    return this.onLoad();
  },
  watch: { // you have to use function syntax here, as warned in the docs
    '$route.params.id': function (newVal, oldVal) {
      this.onLoad();
    }
  }
}
</script>